/**
 * Browser-based download/upload speed measurement using M-Lab NDT7 —
 * the same open protocol behind Google’s “Internet speed test”.
 *
 * Results are general-internet Mbps to the nearest M-Lab server (not a
 * Firebase-specific latency check). Measurements may be contributed to
 * M-Lab’s open dataset per their data policy.
 */

import ndt7 from '@m-lab/ndt7';
import downloadWorkerUrl from '@m-lab/ndt7/src/ndt7-download-worker.js?url';
import uploadWorkerUrl from '@m-lab/ndt7/src/ndt7-upload-worker.js?url';

export interface SpeedTestResult {
  downloadMbps: number;
  uploadMbps: number;
}

export type SpeedTestPhase = 'download' | 'upload';

export interface RunSpeedTestOptions {
  signal?: AbortSignal;
  onPhase?: (phase: SpeedTestPhase) => void;
}

function roundMbps(mbps: number): number {
  if (!Number.isFinite(mbps) || mbps < 0) return 0;
  return Math.round(mbps * 10) / 10;
}

function assertNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Speed test aborted', 'AbortError');
  }
}

function uploadMbpsFromComplete(data: {
  LastServerMeasurement?: {
    TCPInfo?: { BytesReceived?: number; ElapsedTime?: number };
  };
  LastClientMeasurement?: { MeanClientMbps?: number };
}): number {
  const tcp = data?.LastServerMeasurement?.TCPInfo;
  if (
    tcp &&
    typeof tcp.BytesReceived === 'number' &&
    typeof tcp.ElapsedTime === 'number' &&
    tcp.ElapsedTime > 0
  ) {
    // ElapsedTime is microseconds; BytesReceived/ElapsedTime*8 => Mbps.
    return roundMbps((tcp.BytesReceived / tcp.ElapsedTime) * 8);
  }
  const client = data?.LastClientMeasurement?.MeanClientMbps;
  if (typeof client === 'number') return roundMbps(client);
  return 0;
}

/**
 * Run NDT7 download then upload. Pass an AbortSignal to cancel waiting for
 * results when the modal closes (in-flight workers self-terminate shortly after).
 */
export async function runSpeedTest(
  options: RunSpeedTestOptions = {},
): Promise<SpeedTestResult> {
  const { signal, onPhase } = options;
  assertNotAborted(signal);

  return new Promise<SpeedTestResult>((resolve, reject) => {
    let settled = false;
    let downloadMbps = 0;
    let uploadMbps = 0;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      fn();
    };

    const onAbort = () => {
      finish(() => {
        reject(new DOMException('Speed test aborted', 'AbortError'));
      });
    };

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }

    ndt7
      .test(
        {
          userAcceptedDataPolicy: true,
          downloadworkerfile: downloadWorkerUrl,
          uploadworkerfile: uploadWorkerUrl,
          metadata: {
            client_name: 'kingspark-pos',
            client_version:
              typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1',
          },
        },
        {
          error: (msg: string) => {
            finish(() => {
              reject(new Error(String(msg || 'Speed test failed')));
            });
          },
          downloadStart: () => {
            if (!settled) onPhase?.('download');
          },
          uploadStart: () => {
            if (!settled) onPhase?.('upload');
          },
          downloadComplete: (data: {
            LastClientMeasurement?: { MeanClientMbps?: number };
          }) => {
            downloadMbps = roundMbps(
              data?.LastClientMeasurement?.MeanClientMbps ?? 0,
            );
          },
          uploadComplete: (data: {
            LastServerMeasurement?: {
              TCPInfo?: { BytesReceived?: number; ElapsedTime?: number };
            };
            LastClientMeasurement?: { MeanClientMbps?: number };
          }) => {
            uploadMbps = uploadMbpsFromComplete(data);
          },
        },
      )
      .then((code: number) => {
        if (settled) return;
        if (code !== 0) {
          finish(() => {
            reject(new Error('Speed test failed'));
          });
          return;
        }
        if (downloadMbps <= 0 && uploadMbps <= 0) {
          finish(() => {
            reject(new Error('Speed test returned no measurements'));
          });
          return;
        }
        finish(() => {
          resolve({ downloadMbps, uploadMbps });
        });
      })
      .catch((err: unknown) => {
        finish(() => {
          reject(err instanceof Error ? err : new Error(String(err)));
        });
      });
  });
}
